import React, { useState, useEffect, useMemo } from 'react';
import {
  SparklesIcon,
  CheckCircleIcon,
  ClockIcon,
  MagnifyingGlassIcon,
  AcademicCapIcon,
  ArrowPathIcon,
  ArrowDownTrayIcon,
  QuestionMarkCircleIcon,
  TrashIcon,
  TagIcon,
  XMarkIcon,
  DocumentCheckIcon,
  CalendarDaysIcon,
} from '@heroicons/react/24/outline';
import {
  WeeklyQuery,
  WeeklyQueryStats,
  MondayFilterOption,
} from '../../types';
import {
  WEEKLY_QUERY_CATEGORIES,
  subscribeToWeeklyQueries,
  updateWeeklyQueryStatus,
  deleteWeeklyQuery,
  autoSortWeeklyQueries,
  computeWeeklyQueryStats,
  exportWeeklyQueriesToExcel,
  isQueryInMondayWeek,
  formatMondayDisplayName,
  getMondayFilterOptions,
  seedSampleWeeklyQueries,
} from '../../services/firebaseService';

const WeeklyQueriesAdminReport: React.FC = () => {
  const [allQueries, setAllQueries] = useState<WeeklyQuery[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedWeek, setSelectedWeek] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortMode, setSortMode] = useState<'smart_cluster' | 'category' | 'agenda' | 'newest'>('smart_cluster');

  // Admin note modal state
  const [editingQuery, setEditingQuery] = useState<WeeklyQuery | null>(null);
  const [adminNoteText, setAdminNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [seedingData, setSeedingData] = useState(false);

  // Subscribe to real-time queries
  useEffect(() => {
    setLoading(true);
    const unsubscribe = subscribeToWeeklyQueries((list) => {
      setAllQueries(list);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // Compute Monday filter options with counts and relative labels
  const mondayOptions: MondayFilterOption[] = useMemo(() => {
    return getMondayFilterOptions(allQueries);
  }, [allQueries]);

  // Filter queries based on criteria
  const filteredQueries = useMemo(() => {
    let list = allQueries;

    if (selectedWeek !== 'all') {
      list = list.filter((q) => isQueryInMondayWeek(q, selectedWeek));
    }

    if (categoryFilter !== 'all') {
      list = list.filter((q) => q.category === categoryFilter);
    }

    if (statusFilter !== 'all') {
      list = list.filter((q) => q.status === statusFilter);
    }

    if (searchQuery.trim()) {
      const qLower = searchQuery.toLowerCase().trim();
      list = list.filter(
        (q) =>
          q.studentName.toLowerCase().includes(qLower) ||
          q.studentRegNoSuffix.includes(qLower) ||
          q.studentRegistrationNumber.toLowerCase().includes(qLower) ||
          q.questionText.toLowerCase().includes(qLower) ||
          (q.similarityCluster && q.similarityCluster.toLowerCase().includes(qLower)) ||
          (q.instructorName && q.instructorName.toLowerCase().includes(qLower)) ||
          (q.instructorNotes && q.instructorNotes.toLowerCase().includes(qLower)) ||
          (q.adminNotes && q.adminNotes.toLowerCase().includes(qLower))
      );
    }

    return autoSortWeeklyQueries(list, sortMode);
  }, [allQueries, selectedWeek, categoryFilter, statusFilter, searchQuery, sortMode]);

  // Selected cohort queries for stats
  const scopeQueries = useMemo(() => {
    if (selectedWeek === 'all') return allQueries;
    return allQueries.filter((q) => isQueryInMondayWeek(q, selectedWeek));
  }, [allQueries, selectedWeek]);

  const stats: WeeklyQueryStats = useMemo(() => {
    return computeWeeklyQueryStats(scopeQueries);
  }, [scopeQueries]);

  const handleSeedData = async () => {
    setSeedingData(true);
    try {
      const added = await seedSampleWeeklyQueries();
      alert(`Created ${added} sample student questions across This Week, Last Week, and 2 Weeks Ago!`);
    } catch (err) {
      console.error('Failed to seed sample doubts:', err);
      alert('Error creating sample doubts.');
    } finally {
      setSeedingData(false);
    }
  };

  const handleExport = () => {
    exportWeeklyQueriesToExcel(
      filteredQueries,
      selectedWeek === 'all' ? 'All_Cohorts' : selectedWeek
    );
  };

  const handleOpenNoteModal = (query: WeeklyQuery) => {
    setEditingQuery(query);
    setAdminNoteText(query.adminNotes || '');
  };

  const handleSaveAdminNote = async () => {
    if (!editingQuery?.id) return;
    setSavingNote(true);
    try {
      await updateWeeklyQueryStatus(editingQuery.id, {
        adminNotes: adminNoteText.trim(),
      });
      setEditingQuery(null);
      setAdminNoteText('');
    } catch (err) {
      console.error('Failed to save admin note:', err);
      alert('Failed to save admin note.');
    } finally {
      setSavingNote(false);
    }
  };

  const handleDelete = async (query: WeeklyQuery) => {
    if (!query.id) return;
    if (!window.confirm(`Are you sure you want to delete this query from student ${query.studentName}?`)) {
      return;
    }
    try {
      await deleteWeeklyQuery(query.id);
    } catch (err) {
      console.error('Failed to delete query:', err);
      alert('Failed to delete query.');
    }
  };

  return (
    <div className="space-y-6">
      {/* Top Header Card */}
      <div className="bg-white border border-gray-200 rounded-3xl p-6 shadow-sm flex flex-col lg:flex-row lg:items-center justify-between gap-6">
        <div>
          <div className="flex items-center space-x-2 text-xs font-bold text-primary-600 uppercase tracking-wider mb-1">
            <DocumentCheckIcon className="w-4 h-4" />
            <span>Executive Reporting & Oversight</span>
          </div>
          <h2 className="text-xl sm:text-2xl font-black text-gray-900 tracking-tight">
            Weekly Student Questions & Class Addressing Report
          </h2>
          <p className="text-xs sm:text-sm text-gray-500 max-w-2xl mt-1">
            Track student doubts submitted every Monday (up to 3/student), instructor curriculum response rates, and auto-clustered topics for upcoming training sessions.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="bg-gray-50 border border-gray-200 rounded-2xl px-3 py-1.5 flex items-center space-x-2">
            <CalendarDaysIcon className="w-4 h-4 text-primary-600" />
            <span className="text-xs font-semibold text-gray-600">Cohort Monday:</span>
            <select
              value={selectedWeek}
              onChange={(e) => setSelectedWeek(e.target.value)}
              className="bg-transparent text-gray-900 font-bold text-xs sm:text-sm focus:outline-none cursor-pointer max-w-[220px] truncate"
            >
              <option value="all">All Recorded Mondays ({allQueries.length})</option>
              {mondayOptions.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.fullLabel} {m.count !== undefined ? `— [${m.count} Doubts]` : ''}
                </option>
              ))}
            </select>
          </div>

          {allQueries.length === 0 && (
            <button
              onClick={handleSeedData}
              disabled={seedingData}
              className="px-3.5 py-2.5 rounded-2xl bg-amber-50 hover:bg-amber-100 text-amber-800 border border-amber-300 text-xs font-bold transition flex items-center space-x-1.5 cursor-pointer shadow-sm"
              title="Populate test student questions for This Week, Last Week & 2 Weeks Ago"
            >
              <SparklesIcon className="w-4 h-4 text-amber-600" />
              <span>{seedingData ? 'Generating...' : 'Seed Sample Doubts'}</span>
            </button>
          )}

          <button
            onClick={handleExport}
            className="px-4 py-2.5 rounded-2xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold transition flex items-center space-x-2 shadow-sm cursor-pointer"
            title="Download full Excel spreadsheet report"
          >
            <ArrowDownTrayIcon className="w-4 h-4" />
            <span>Export to Excel (.xlsx)</span>
          </button>
        </div>
      </div>

      {/* Dedicated Monday Quick-Filter Bar for Admin */}
      <div className="bg-white border border-gray-200 rounded-2xl p-4 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 mb-3">
          <div className="flex items-center space-x-2">
            <CalendarDaysIcon className="w-4 h-4 text-primary-600" />
            <h3 className="text-xs sm:text-sm font-bold text-gray-900 uppercase tracking-wide">
              Filter by Monday Submission Date (हफ्ते के अनुसार फ़िल्टर)
            </h3>
          </div>
          <span className="text-xs text-gray-500">
            Select any Monday cohort to inspect questions asked by students
          </span>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* All Mondays Pill */}
          <button
            type="button"
            onClick={() => setSelectedWeek('all')}
            className={`px-3.5 py-2 rounded-xl text-xs font-bold transition flex items-center space-x-2 border cursor-pointer ${selectedWeek === 'all'
                ? 'bg-primary-600 text-white border-primary-600 shadow-sm'
                : 'bg-gray-50 hover:bg-gray-100 text-gray-700 border-gray-200'
              }`}
          >
            <span>All Recorded Mondays</span>
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-mono font-bold ${selectedWeek === 'all'
                ? 'bg-white/20 text-white'
                : 'bg-gray-200 text-gray-700'
              }`}>
              {allQueries.length}
            </span>
          </button>

          {/* Individual Monday Filter Buttons */}
          {mondayOptions.slice(0, 5).map((m) => {
            const isSelected = selectedWeek === m.id || selectedWeek === m.mondayDate;
            return (
              <button
                key={m.id}
                type="button"
                onClick={() => setSelectedWeek(m.id)}
                className={`px-3.5 py-2 rounded-xl text-xs font-bold transition flex items-center space-x-2 border cursor-pointer ${isSelected
                    ? 'bg-primary-600 text-white border-primary-600 shadow-sm'
                    : 'bg-gray-50 hover:bg-gray-100 text-gray-700 border-gray-200'
                  }`}
              >
                <span>{m.relativeLabel}</span>
                <span className="text-[11px] opacity-75 font-normal">
                  ({m.formattedDate.replace(/^[A-Za-z]+, /, '')})
                </span>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-mono font-bold ${isSelected
                    ? 'bg-white/20 text-white'
                    : m.count && m.count > 0
                      ? 'bg-primary-100 text-primary-800'
                      : 'bg-gray-200 text-gray-500'
                  }`}>
                  {m.count || 0}
                </span>
              </button>
            );
          })}

          {/* Dropdown if more than 5 past Mondays */}
          {mondayOptions.length > 5 && (
            <select
              value={mondayOptions.slice(5).some(m => m.id === selectedWeek || m.mondayDate === selectedWeek) ? selectedWeek : ''}
              onChange={(e) => {
                if (e.target.value) setSelectedWeek(e.target.value);
              }}
              className="bg-gray-50 border border-gray-200 text-gray-700 text-xs font-bold rounded-xl px-3 py-2 focus:outline-none cursor-pointer"
            >
              <option value="">More Previous Mondays...</option>
              {mondayOptions.slice(5).map((m) => (
                <option key={m.id} value={m.id}>
                  {m.fullLabel} ({m.count || 0})
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      {/* Analytics KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm">
          <div className="flex items-center justify-between text-gray-500 text-xs font-bold uppercase tracking-wider mb-1">
            <span>Total Questions</span>
            <QuestionMarkCircleIcon className="w-4 h-4 text-primary-600" />
          </div>
          <div className="text-3xl font-black text-gray-900">
            {stats.totalQueries}
          </div>
          <p className="text-xs text-gray-500 mt-1">
            From {stats.uniqueStudents} unique student{stats.uniqueStudents !== 1 ? 's' : ''}
          </p>
        </div>

        <div className="bg-white border border-amber-200 rounded-2xl p-5 shadow-sm bg-gradient-to-br from-amber-50/50 to-white">
          <div className="flex items-center justify-between text-amber-700 text-xs font-bold uppercase tracking-wider mb-1">
            <span>Pending Review</span>
            <ClockIcon className="w-4 h-4 text-amber-600" />
          </div>
          <div className="text-3xl font-black text-amber-700">
            {stats.pendingCount}
          </div>
          <p className="text-xs text-amber-600 mt-1">
            Waiting for instructor assignment
          </p>
        </div>

        <div className="bg-white border border-blue-200 rounded-2xl p-5 shadow-sm bg-gradient-to-br from-blue-50/50 to-white">
          <div className="flex items-center justify-between text-blue-700 text-xs font-bold uppercase tracking-wider mb-1">
            <span>Scheduled for Next Class</span>
            <AcademicCapIcon className="w-4 h-4 text-blue-600" />
          </div>
          <div className="text-3xl font-black text-blue-700">
            {stats.plannedForClassCount}
          </div>
          <p className="text-xs text-blue-600 mt-1">
            Active in upcoming lecture agendas
          </p>
        </div>

        <div className="bg-white border border-emerald-200 rounded-2xl p-5 shadow-sm bg-gradient-to-br from-emerald-50/50 to-white">
          <div className="flex items-center justify-between text-emerald-700 text-xs font-bold uppercase tracking-wider mb-1">
            <span>Addressed in Class</span>
            <CheckCircleIcon className="w-4 h-4 text-emerald-600" />
          </div>
          <div className="text-3xl font-black text-emerald-700">
            {stats.addressedCount}
          </div>
          <p className="text-xs text-emerald-600 mt-1">
            {stats.totalQueries > 0
              ? `${Math.round((stats.addressedCount / stats.totalQueries) * 100)}% resolution rate`
              : '0% completed'}
          </p>
        </div>
      </div>

      {/* Category Breakdown Bar Widget */}
      <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm">
        <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-3">
          Topic Category Distribution
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {WEEKLY_QUERY_CATEGORIES.map((cat) => {
            const count = stats.byCategory[cat.id] || 0;
            const pct = stats.totalQueries > 0 ? Math.round((count / stats.totalQueries) * 100) : 0;
            return (
              <div key={cat.id} className="p-3 rounded-xl bg-gray-50 border border-gray-100 flex flex-col justify-between">
                <div className="flex items-center space-x-1.5 text-xs font-bold text-gray-800 truncate">
                  <span>{cat.icon}</span>
                  <span className="truncate">{cat.label.split('&')[0]}</span>
                </div>
                <div className="mt-2 flex items-baseline justify-between">
                  <span className="text-lg font-black text-gray-900">{count}</span>
                  <span className="text-[10px] text-gray-500 font-semibold">{pct}%</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div className="bg-white border border-gray-200 rounded-2xl p-4 shadow-sm flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-4">
        {/* Search */}
        <div className="relative flex-1">
          <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by student name, ID, question, topic cluster, instructor..."
            className="w-full pl-10 pr-4 py-2 rounded-xl bg-gray-50 border border-gray-200 text-gray-900 text-xs placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Filter controls */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Automatic Sort */}
          <div className="flex items-center space-x-1.5 bg-indigo-50 border border-indigo-200 rounded-xl px-2.5 py-1.5 text-xs text-indigo-900">
            <SparklesIcon className="w-3.5 h-3.5 text-indigo-600" />
            <span className="font-semibold">Sort:</span>
            <select
              value={sortMode}
              onChange={(e: any) => setSortMode(e.target.value)}
              className="bg-transparent font-bold focus:outline-none cursor-pointer"
            >
              <option value="smart_cluster">⚡ Topic Cluster</option>
              <option value="agenda">🎯 Next Class First</option>
              <option value="category">📁 Category</option>
              <option value="newest">🕒 Newest</option>
            </select>
          </div>

          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="px-3 py-1.5 rounded-xl bg-gray-50 border border-gray-200 text-gray-700 text-xs font-medium focus:outline-none cursor-pointer"
          >
            <option value="all">All Categories</option>
            {WEEKLY_QUERY_CATEGORIES.map((c) => (
              <option key={c.id} value={c.id}>{c.icon} {c.label}</option>
            ))}
          </select>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-1.5 rounded-xl bg-gray-50 border border-gray-200 text-gray-700 text-xs font-medium focus:outline-none cursor-pointer"
          >
            <option value="all">All Statuses</option>
            <option value="pending">⏳ Pending Review</option>
            <option value="planned_for_class">🎯 Scheduled for Next Class</option>
            <option value="addressed_in_class">✅ Addressed in Class</option>
          </select>
        </div>
      </div>

      {/* Queries Table */}
      <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between text-xs text-gray-500 font-semibold">
          <span>Showing {filteredQueries.length} of {allQueries.length} total questions</span>
          <span>Week: {selectedWeek}</span>
        </div>

        {loading ? (
          <div className="py-16 text-center text-gray-500 flex flex-col items-center justify-center space-y-2">
            <ArrowPathIcon className="w-8 h-8 animate-spin text-primary-600" />
            <span className="text-xs">Loading weekly questions database...</span>
          </div>
        ) : filteredQueries.length === 0 ? (
          <div className="py-16 text-center text-gray-400 space-y-2">
            <QuestionMarkCircleIcon className="w-10 h-10 mx-auto text-gray-300" />
            <p className="text-sm font-bold text-gray-600">No student queries found</p>
            <p className="text-xs">Try clearing search filters or changing the cohort week.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-xs">
              <thead className="bg-gray-50 text-gray-600 font-bold uppercase tracking-wider text-[11px]">
                <tr>
                  <th className="py-3 px-4 text-left">Student</th>
                  <th className="py-3 px-4 text-left">Question Details</th>
                  <th className="py-3 px-4 text-left">Topic & Cluster</th>
                  <th className="py-3 px-4 text-left">Status & Assignment</th>
                  <th className="py-3 px-4 text-left">Class Notes</th>
                  <th className="py-3 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredQueries.map((q, idx) => (
                  <tr key={q.id || idx} className="hover:bg-gray-50/80 transition">
                    {/* Student */}
                    <td className="py-3 px-4 align-top">
                      <div className="font-bold text-gray-900">{q.studentName}</div>
                      <div className="text-[10px] font-mono text-primary-600 font-semibold mt-0.5">
                        ID: {q.studentRegNoSuffix}
                      </div>
                      <div className="text-[10px] text-gray-400 truncate max-w-[130px]">
                        {q.studentBatchLabel || 'Candidate'}
                      </div>
                      <span className="inline-block mt-1 text-[9px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 font-mono">
                        Q{q.questionNumber || 1}/3
                      </span>
                    </td>

                    {/* Question details */}
                    <td className="py-3 px-4 align-top max-w-sm">
                      <p className="text-gray-900 font-medium leading-relaxed bg-gray-50 p-2.5 rounded-xl border border-gray-100">
                        "{q.questionText}"
                      </p>
                      <div className="flex flex-wrap items-center gap-2 text-[10px] text-gray-400 mt-1">
                        <span>Submitted: {q.submittedAtStr || 'Recently'}</span>
                        <span className="inline-flex items-center space-x-1 px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 font-medium border border-indigo-100">
                          <CalendarDaysIcon className="w-3 h-3 text-indigo-600" />
                          <span>Monday: {formatMondayDisplayName(q.weekMondayDate || q.weekIdentifier)}</span>
                        </span>
                      </div>
                    </td>

                    {/* Category & Cluster */}
                    <td className="py-3 px-4 align-top">
                      <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold bg-primary-50 text-primary-700 border border-primary-100">
                        {q.categoryLabel || q.category}
                      </span>
                      {q.similarityCluster && (
                        <div className="text-[11px] text-gray-600 mt-1.5 flex items-center space-x-1">
                          <TagIcon className="w-3 h-3 text-indigo-500 flex-shrink-0" />
                          <span className="font-medium">{q.similarityCluster}</span>
                        </div>
                      )}
                    </td>

                    {/* Status & Assignment */}
                    <td className="py-3 px-4 align-top">
                      {q.status === 'planned_for_class' ? (
                        <span className="inline-flex items-center space-x-1 px-2.5 py-1 rounded-full text-[10px] font-extrabold bg-blue-100 text-blue-800 border border-blue-200">
                          <AcademicCapIcon className="w-3.5 h-3.5" />
                          <span>Selected for Next Class</span>
                        </span>
                      ) : q.status === 'addressed_in_class' ? (
                        <span className="inline-flex items-center space-x-1 px-2.5 py-1 rounded-full text-[10px] font-extrabold bg-emerald-100 text-emerald-800 border border-emerald-200">
                          <CheckCircleIcon className="w-3.5 h-3.5" />
                          <span>Addressed in Class</span>
                        </span>
                      ) : (
                        <span className="inline-flex items-center space-x-1 px-2.5 py-1 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-800 border border-amber-200">
                          <ClockIcon className="w-3.5 h-3.5" />
                          <span>Pending Review</span>
                        </span>
                      )}

                      {q.instructorName && (
                        <div className="text-[11px] text-gray-700 font-medium mt-1">
                          👤 {q.instructorName}
                        </div>
                      )}
                    </td>

                    {/* Notes */}
                    <td className="py-3 px-4 align-top max-w-xs">
                      {q.instructorNotes ? (
                        <div className="p-2 rounded-lg bg-indigo-50/60 border border-indigo-100 text-[11px] text-indigo-900">
                          <strong className="block text-[10px] text-indigo-700 font-bold uppercase">Class Talking Point:</strong>
                          {q.instructorNotes}
                        </div>
                      ) : (
                        <span className="text-gray-400 italic text-[11px]">No instructor note</span>
                      )}

                      {q.adminNotes && (
                        <div className="mt-1 p-1.5 rounded bg-amber-50 text-[10px] text-amber-900 border border-amber-200">
                          <strong>Admin:</strong> {q.adminNotes}
                        </div>
                      )}
                    </td>

                    {/* Actions */}
                    <td className="py-3 px-4 align-top text-right space-x-1">
                      <button
                        onClick={() => handleOpenNoteModal(q)}
                        className="px-2.5 py-1 rounded-lg text-[11px] font-semibold bg-gray-100 hover:bg-gray-200 text-gray-700 transition"
                        title="Add admin internal note"
                      >
                        Note
                      </button>

                      <button
                        onClick={() => handleDelete(q)}
                        className="p-1 rounded-lg text-red-500 hover:bg-red-50 transition"
                        title="Delete question"
                      >
                        <TrashIcon className="w-4 h-4 inline" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Admin Note Modal */}
      {editingQuery && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in">
          <div className="bg-white rounded-3xl p-6 w-full max-w-md shadow-2xl space-y-4">
            <div className="flex items-center justify-between pb-3 border-b border-gray-100">
              <h3 className="font-bold text-gray-900 text-sm sm:text-base">
                Internal Admin Note
              </h3>
              <button
                onClick={() => setEditingQuery(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="text-xs text-gray-600 space-y-1">
              <p><strong>Candidate:</strong> {editingQuery.studentName} (ID: {editingQuery.studentRegNoSuffix})</p>
              <p className="bg-gray-50 p-2.5 rounded-xl border border-gray-100 text-gray-800">
                "{editingQuery.questionText}"
              </p>
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">
                Admin Remark / Internal Action
              </label>
              <textarea
                value={adminNoteText}
                onChange={(e) => setAdminNoteText(e.target.value)}
                rows={3}
                placeholder="e.g. Forwarded to Head Coach for Wednesday video class demonstration..."
                className="w-full px-3 py-2 rounded-xl bg-gray-50 border border-gray-200 text-gray-900 text-xs focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div className="flex items-center justify-end space-x-2 pt-2">
              <button
                type="button"
                onClick={() => setEditingQuery(null)}
                className="px-4 py-2 rounded-xl text-xs font-medium text-gray-600 hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveAdminNote}
                disabled={savingNote}
                className="px-4 py-2 rounded-xl bg-primary-600 hover:bg-primary-500 text-white text-xs font-bold shadow-sm"
              >
                {savingNote ? 'Saving...' : 'Save Remark'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WeeklyQueriesAdminReport;
